import { formatUnits, parseUnits } from "@ethersproject/units";
import { useEthers } from "@usedapp/core";
import { Contract } from "ethers";
import { useCallback, useEffect, useState } from "react";
import useSWR from "swr";
import { useSnackbar } from "../../components/Snackbar";

import abi from "../abis/contract_abi.json";

export const CONTRACT_ADDRESS = "0xe46aeb8952ec91ce06508a3c6f363bdb053bed11";

function useContractMethods<T>(
  contract: Contract | null,
  methods: string,
  callback?: (result: T) => any,
  dependencies?: any[]
) {
  return useSWR<T>(
    contract && [CONTRACT_ADDRESS, methods, ...(dependencies || [])],
    async () => {
      const result = await contract?.[methods]?.();
      if (typeof callback === "function") {
        return callback(result);
      }
      return result;
    }
  );
}

/**
 * TODO: Implement useContract hook
 */
export default function useContract() {
  const [contract, setContract] = useState<Contract | null>(null);
  const { library, chainId } = useEthers();
  const { openSnackbar } = useSnackbar();

  const { data: cost } = useContractMethods<string>(
    contract,
    "cost",
    (result) => formatUnits(result, "ether"),
    [chainId]
  );

  const { data: isPaused } = useContractMethods<boolean>(
    contract,
    "paused",
    (r) => r,
    [chainId]
  );

  const initContract = useCallback(async () => {
    const _contract = new Contract(
      CONTRACT_ADDRESS,
      abi,
      library?.getSigner?.()
    );
    await _contract.cost();
    setContract(_contract);
  }, [library]);

  const handleMint = useCallback(async () => {
    if (cost && library && contract) {
      try {
        const tx = await contract.mint(1, {
          value: parseUnits(cost, "ether"),
        });
        const scanner =
          chainId === 1
            ? "https://etherscan.io/tx/"
            : "https://rinkeby.etherscan.io/tx/";
        openSnackbar(
          <div>
            Transaction submitted,{" "}
            <a
              href={scanner + tx.hash}
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              open in explorer
            </a>
          </div>,
          "success"
        );
      } catch (error) {
        console.log(error);
        if ((error as Error).message) {
          openSnackbar(<div>{(error as Error).message}</div>, "error");
        }
      }
    }
  }, [chainId, contract, cost, library, openSnackbar]);

  /**
   * Init contract
   */
  useEffect(() => {
    if (contract === null) {
      initContract();
    }
  }, [initContract, contract]);

  return {
    contract,
    cost,
    mint: handleMint,
    isPaused,
    isLoading: !cost,
  };
}
